import { useRef, useState, useCallback } from 'react';

interface InteractiveCardProps {
  children: React.ReactNode;
  className?: string;
}

export function InteractiveCard({ children, className = '' }: InteractiveCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [borderHighlights, setBorderHighlights] = useState({
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const calculateBorderHighlights = useCallback((x: number, y: number, rect: DOMRect) => {
    const { width, height } = rect;
    const threshold = 60; // Distance threshold for highlighting
    
    // Calculate distances to corners
    const distToTopLeft = Math.sqrt(x * x + y * y);
    const distToTopRight = Math.sqrt((width - x) * (width - x) + y * y);
    const distToBottomLeft = Math.sqrt(x * x + (height - y) * (height - y));
    const distToBottomRight = Math.sqrt((width - x) * (width - x) + (height - y) * (height - y));
    
    // Calculate distances to edges
    const distToTop = Math.abs(y);
    const distToRight = Math.abs(width - x);
    const distToBottom = Math.abs(height - y);
    const distToLeft = Math.abs(x);
    
    // Calculate highlight intensities (0-1) based on proximity
    const cornerIntensity = (distance: number) => Math.max(0, 1 - distance / threshold);
    const edgeIntensity = (distance: number, parallelPos: number, edgeLength: number) => {
      const proximityFactor = Math.max(0, 1 - distance / threshold);
      const edgeFactor = Math.max(0, 1 - Math.abs(parallelPos - edgeLength / 2) / (edgeLength / 2));
      return proximityFactor * edgeFactor * 0.6; // Slightly dimmer than corners
    };
    
    return {
      topLeft: cornerIntensity(distToTopLeft),
      topRight: cornerIntensity(distToTopRight),
      bottomLeft: cornerIntensity(distToBottomLeft),
      bottomRight: cornerIntensity(distToBottomRight),
      top: edgeIntensity(distToTop, x, width),
      right: edgeIntensity(distToRight, y, height),
      bottom: edgeIntensity(distToBottom, x, width),
      left: edgeIntensity(distToLeft, y, height),
    };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const highlights = calculateBorderHighlights(x, y, rect);
    setBorderHighlights(highlights);
  }, [calculateBorderHighlights]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setBorderHighlights({
      topLeft: 0,
      topRight: 0,
      bottomLeft: 0,
      bottomRight: 0,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    });
  }, []);

  const cornerSize = 20; // Size of corner highlights
  const borderWidth = 2; // Width of border highlights

  return (
    <div
      ref={cardRef}
      className={`relative overflow-hidden rounded-lg border border-border/50 bg-card transition-all duration-300 group ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Corner highlights */}
      {isHovered && (
        <>
          {/* Top-left corner */}
          <div
            className="absolute top-0 left-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${cornerSize}px`,
              height: `${cornerSize}px`,
              background: `radial-gradient(circle at 0% 0%, rgba(0, 212, 255, ${borderHighlights.topLeft * 0.8}), transparent 70%)`,
              opacity: borderHighlights.topLeft,
            }}
          />
          
          {/* Top-right corner */}
          <div
            className="absolute top-0 right-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${cornerSize}px`,
              height: `${cornerSize}px`,
              background: `radial-gradient(circle at 100% 0%, rgba(0, 212, 255, ${borderHighlights.topRight * 0.8}), transparent 70%)`,
              opacity: borderHighlights.topRight,
            }}
          />
          
          {/* Bottom-left corner */}
          <div
            className="absolute bottom-0 left-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${cornerSize}px`,
              height: `${cornerSize}px`,
              background: `radial-gradient(circle at 0% 100%, rgba(0, 212, 255, ${borderHighlights.bottomLeft * 0.8}), transparent 70%)`,
              opacity: borderHighlights.bottomLeft,
            }}
          />
          
          {/* Bottom-right corner */}
          <div
            className="absolute bottom-0 right-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${cornerSize}px`,
              height: `${cornerSize}px`,
              background: `radial-gradient(circle at 100% 100%, rgba(0, 212, 255, ${borderHighlights.bottomRight * 0.8}), transparent 70%)`,
              opacity: borderHighlights.bottomRight,
            }}
          />
          
          {/* Top edge */}
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none transition-opacity duration-200"
            style={{
              height: `${borderWidth}px`,
              background: `linear-gradient(90deg, transparent, rgba(0, 212, 255, ${borderHighlights.top}), transparent)`,
              opacity: borderHighlights.top,
            }}
          />
          
          {/* Right edge */}
          <div
            className="absolute top-0 right-0 bottom-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${borderWidth}px`,
              background: `linear-gradient(180deg, transparent, rgba(0, 212, 255, ${borderHighlights.right}), transparent)`,
              opacity: borderHighlights.right,
            }}
          />
          
          {/* Bottom edge */}
          <div
            className="absolute bottom-0 left-0 right-0 pointer-events-none transition-opacity duration-200"
            style={{
              height: `${borderWidth}px`,
              background: `linear-gradient(90deg, transparent, rgba(0, 212, 255, ${borderHighlights.bottom}), transparent)`,
              opacity: borderHighlights.bottom,
            }}
          />
          
          {/* Left edge */}
          <div
            className="absolute top-0 left-0 bottom-0 pointer-events-none transition-opacity duration-200"
            style={{
              width: `${borderWidth}px`,
              background: `linear-gradient(180deg, transparent, rgba(0, 212, 255, ${borderHighlights.left}), transparent)`,
              opacity: borderHighlights.left,
            }}
          />
        </>
      )}
      
      {/* Main border overlay for subtle overall glow */}
      <div 
        className={`absolute inset-0 rounded-lg border transition-opacity duration-300 pointer-events-none ${
          isHovered ? 'border-primary/20 opacity-100' : 'border-transparent opacity-0'
        }`}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}